'use client';

import { type ChangeEvent, type ComponentProps, type ReactNode, useEffect, useMemo, useState } from 'react';

import {
  AsYouType,
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

import { type ControlState } from '@/components/common/Control';
import { Flex } from '@/components/common/Flex';
import { TextInput } from '@/components/common/input/TextInput';
import { Select } from '@/components/common/select/Select';
import { useLocale } from '@/hooks/use-locale';
import { type Locale } from '@/i18n/routing';

import styles from './PhoneInput.module.scss';

const DEFAULT_COUNTRY_BY_LOCALE: Record<Locale, CountryCode> = {
  en: 'US',
  ru: 'RU',
};

const getCountryFlag = (country: string) =>
  country.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const toE164 = (country: CountryCode, nationalNumber: string) => {
  const trimmed = nationalNumber.trim();

  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('+')) {
    return parsePhoneNumberFromString(trimmed)?.number ?? '';
  }

  const digits = trimmed.replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  return `+${getCountryCallingCode(country)}${digits}`;
};

const CountryLabel = ({ flag, children }: { flag: string; children: ReactNode }) => (
  <span className={styles.countryLabel}>
    <span>{flag}</span>
    <span>{children}</span>
  </span>
);

export type PhoneInputProps = Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'> & {
  value?: string;
  onChange?: (value: string) => void;
  state?: ControlState;
  variant?: 'default' | 'box';
};

export const PhoneInput = (props: PhoneInputProps) => {
  const {
    className,
    value = '',
    onChange,
    onBlur,
    state = 'default',
    variant = 'default',
    disabled,
    required,
    placeholder,
    name,
    id,
    autoComplete = 'tel',
  } = props;

  const locale = useLocale();
  const defaultCountry = DEFAULT_COUNTRY_BY_LOCALE[locale];

  const [country, setCountry] = useState<CountryCode>(
    () => parsePhoneNumberFromString(value)?.country ?? defaultCountry
  );
  const [nationalNumber, setNationalNumber] = useState(() => parsePhoneNumberFromString(value)?.formatNational() ?? '');
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setMenuPortalTarget(document.body);
  }, []);

  const countryOptions = useMemo(() => {
    const regionNames = new Intl.DisplayNames([locale], { type: 'region' });

    return getCountries()
      .map((countryCode) => {
        const callingCode = getCountryCallingCode(countryCode);
        const countryName = regionNames.of(countryCode) ?? countryCode;
        const flag = getCountryFlag(countryCode);

        return { countryCode, callingCode, countryName, flag };
      })
      .sort((left, right) => left.countryName.localeCompare(right.countryName, locale))
      .map(({ countryCode, callingCode, countryName, flag }) => ({
        value: countryCode,
        node: (
          <CountryLabel flag={flag}>
            {countryName} (+{callingCode})
          </CountryLabel>
        ),
        selectedNode: <CountryLabel flag={flag}>+{callingCode}</CountryLabel>,
      }));
  }, [locale]);

  const emitChange = (nextCountry: CountryCode, nextNationalNumber: string) => {
    onChange?.(toE164(nextCountry, nextNationalNumber));
  };

  const handleCountryChange = (nextCountry: string) => {
    const countryCode = nextCountry as CountryCode;
    const digits = nationalNumber.replace(/\D/g, '');
    const formatter = new AsYouType(countryCode);
    const formatted = digits ? formatter.input(digits) : '';

    setCountry(countryCode);
    setNationalNumber(formatted);
    emitChange(countryCode, formatted);
  };

  const handleNationalNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;

    if (raw.trim().startsWith('+')) {
      const parsed = parsePhoneNumberFromString(raw);

      if (parsed?.country) {
        const formatted = parsed.formatNational();
        setCountry(parsed.country);
        setNationalNumber(formatted);
        onChange?.(parsed.number);

        return;
      }
    }

    const formatter = new AsYouType(country);
    const formatted = formatter.input(raw);

    setNationalNumber(formatted);
    emitChange(country, formatted);
  };

  return (
    <Flex className={className} alignItems="flexEnd" gap={3}>
      <Select
        className={styles.country}
        menuClassName={styles.countryMenu}
        fitMenuContent
        closeMenuOnSelect
        menuPortalTarget={menuPortalTarget}
        variant={variant}
        state={state}
        value={country}
        options={countryOptions}
        disabled={disabled}
        instanceId={name ? `${name}-country` : 'phone-country'}
        onChange={handleCountryChange}
      />

      <TextInput
        className={styles.number}
        variant={variant}
        state={state}
        type="tel"
        inputMode="tel"
        autoComplete={autoComplete}
        name={name}
        id={id}
        value={nationalNumber}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        onChange={handleNationalNumberChange}
        onBlur={onBlur}
      />
    </Flex>
  );
};
